import type {
  Adjustments,
  EvapType,
  Evaporation,
  SnowmeltClimatology,
  TempSource,
  Temperature,
} from "../model/inp/climatology.ts";
import type { TimeSeries } from "../model/inp/timeseries.ts";
import type { Pattern } from "../model/inp/patterns.ts";
import { NumberField, NumberInput } from "./fields.tsx";
import { useT } from "../i18n/hooks.ts";

const MONTH_KEYS = [
  "climate.jan",
  "climate.feb",
  "climate.mar",
  "climate.apr",
  "climate.may",
  "climate.jun",
  "climate.jul",
  "climate.aug",
  "climate.sep",
  "climate.oct",
  "climate.nov",
  "climate.dec",
] as const;

const SNOWMELT_DEFAULTS: SnowmeltClimatology = {
  snowTemp: 0,
  tipm: 0,
  rnm: 0,
  elevation: 0,
  latitude: 0,
  dtLongitude: 0,
};

// [EVAPORATION]/[TEMPERATURE]/[ADJUSTMENTS] are each one project-wide
// setup, not a list of named objects, so unlike every other tab this is a
// flat settings form rather than list+detail.
export function ClimatologyEditor({
  evaporation,
  onChangeEvaporation,
  temperature,
  onChangeTemperature,
  adjustments,
  onChangeAdjustments,
  timeSeries,
  patterns,
}: {
  evaporation: Evaporation;
  onChangeEvaporation: (evap: Evaporation) => void;
  temperature: Temperature;
  onChangeTemperature: (temp: Temperature) => void;
  adjustments: Adjustments;
  onChangeAdjustments: (adj: Adjustments) => void;
  timeSeries: TimeSeries[];
  patterns: Pattern[];
}) {
  const t = useT();

  return (
    <div className="named-collection-detail">
      <section className="property-panel-group">
        <h3>{t("climate.evaporation")}</h3>
        <div className="named-collection-fields">
          <label className="options-field">
            {t("climate.evapType")}
            <select
              value={evaporation.type}
              onChange={(e) =>
                onChangeEvaporation({ ...evaporation, type: e.target.value as EvapType })
              }
            >
              <option value="CONSTANT">{t("climate.evapTypeConstant")}</option>
              <option value="MONTHLY">{t("climate.evapTypeMonthly")}</option>
              <option value="TIMESERIES">{t("climate.evapTypeTimeSeries")}</option>
              <option value="FILE">{t("climate.evapTypeFile")}</option>
              <option value="TEMPERATURE">{t("climate.evapTypeTemperature")}</option>
            </select>
          </label>
          {evaporation.type === "CONSTANT" && (
            <label className="options-field">
              {t("climate.evapConstantValue")}
              <NumberInput
                value={evaporation.constantValue}
                onChange={(v) => onChangeEvaporation({ ...evaporation, constantValue: v })}
              />
            </label>
          )}
          {evaporation.type === "TIMESERIES" && (
            <label className="options-field">
              {t("climate.evapTimeSeries")}
              <select
                value={evaporation.timeSeries}
                onChange={(e) =>
                  onChangeEvaporation({ ...evaporation, timeSeries: e.target.value })
                }
              >
                <option value="" />
                {timeSeries.map((ts) => (
                  <option key={ts.name} value={ts.name}>
                    {ts.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="options-field">
            {t("climate.recoveryPattern")}
            <select
              value={evaporation.recoveryPattern}
              onChange={(e) =>
                onChangeEvaporation({ ...evaporation, recoveryPattern: e.target.value })
              }
            >
              <option value="">{t("climate.recoveryPatternNone")}</option>
              {patterns.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="options-field options-field-checkbox">
            <input
              type="checkbox"
              checked={evaporation.dryOnly}
              onChange={(e) => onChangeEvaporation({ ...evaporation, dryOnly: e.target.checked })}
            />
            {t("climate.dryOnly")}
          </label>
        </div>
        {evaporation.type === "MONTHLY" && (
          <MonthlyTable
            values={evaporation.monthlyValues}
            onChange={(monthlyValues) => onChangeEvaporation({ ...evaporation, monthlyValues })}
          />
        )}
      </section>

      <section className="property-panel-group">
        <h3>{t("climate.temperature")}</h3>
        <div className="named-collection-fields">
          <label className="options-field">
            {t("climate.tempSource")}
            <select
              value={temperature.source}
              onChange={(e) =>
                onChangeTemperature({ ...temperature, source: e.target.value as TempSource })
              }
            >
              <option value="">{t("climate.tempSourceNone")}</option>
              <option value="TIMESERIES">{t("climate.tempSourceTimeSeries")}</option>
              <option value="FILE">{t("climate.tempSourceFile")}</option>
            </select>
          </label>
          {temperature.source === "TIMESERIES" && (
            <label className="options-field">
              {t("climate.tempTimeSeries")}
              <select
                value={temperature.timeSeries}
                onChange={(e) =>
                  onChangeTemperature({ ...temperature, timeSeries: e.target.value })
                }
              >
                <option value="" />
                {timeSeries.map((ts) => (
                  <option key={ts.name} value={ts.name}>
                    {ts.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {temperature.source === "FILE" && (
            <>
              <label className="options-field">
                {t("climate.tempFileName")}
                <input
                  type="text"
                  value={temperature.fileName}
                  onChange={(e) =>
                    onChangeTemperature({ ...temperature, fileName: e.target.value })
                  }
                />
              </label>
              <label className="options-field">
                {t("climate.tempFileStartDate")}
                <input
                  type="text"
                  value={temperature.fileStartDate}
                  onChange={(e) =>
                    onChangeTemperature({ ...temperature, fileStartDate: e.target.value })
                  }
                />
              </label>
            </>
          )}
        </div>

        <h4 className="named-collection-subsection-title lid-layer-toggle">
          <label>
            <input
              type="checkbox"
              checked={temperature.snowmelt !== null}
              onChange={(e) =>
                onChangeTemperature({
                  ...temperature,
                  snowmelt: e.target.checked ? { ...SNOWMELT_DEFAULTS } : null,
                })
              }
            />
            {t("climate.snowmelt")}
          </label>
        </h4>
        {temperature.snowmelt && (
          <div className="named-collection-fields">
            <NumberField
              label={t("climate.snowTemp")}
              value={temperature.snowmelt.snowTemp}
              onChange={(v) =>
                onChangeTemperature({
                  ...temperature,
                  snowmelt: { ...temperature.snowmelt!, snowTemp: v },
                })
              }
            />
            <NumberField
              label={t("climate.tipm")}
              value={temperature.snowmelt.tipm}
              onChange={(v) =>
                onChangeTemperature({
                  ...temperature,
                  snowmelt: { ...temperature.snowmelt!, tipm: v },
                })
              }
            />
            <NumberField
              label={t("climate.rnm")}
              value={temperature.snowmelt.rnm}
              onChange={(v) =>
                onChangeTemperature({
                  ...temperature,
                  snowmelt: { ...temperature.snowmelt!, rnm: v },
                })
              }
            />
            <NumberField
              label={t("climate.elevation")}
              value={temperature.snowmelt.elevation}
              onChange={(v) =>
                onChangeTemperature({
                  ...temperature,
                  snowmelt: { ...temperature.snowmelt!, elevation: v },
                })
              }
            />
            <NumberField
              label={t("climate.latitude")}
              value={temperature.snowmelt.latitude}
              onChange={(v) =>
                onChangeTemperature({
                  ...temperature,
                  snowmelt: { ...temperature.snowmelt!, latitude: v },
                })
              }
            />
            <NumberField
              label={t("climate.dtLongitude")}
              value={temperature.snowmelt.dtLongitude}
              onChange={(v) =>
                onChangeTemperature({
                  ...temperature,
                  snowmelt: { ...temperature.snowmelt!, dtLongitude: v },
                })
              }
            />
          </div>
        )}
      </section>

      <section className="property-panel-group">
        <h3>{t("climate.adjustments")}</h3>
        <div className="named-collection-points-table-wrap">
          <table className="named-collection-points-table">
            <thead>
              <tr>
                <th />
                {MONTH_KEYS.map((key) => (
                  <th key={key}>{t(key)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <AdjustmentRow
                label={t("climate.adjustTemperature")}
                values={adjustments.temperature}
                onChange={(temperature2) =>
                  onChangeAdjustments({ ...adjustments, temperature: temperature2 })
                }
              />
              <AdjustmentRow
                label={t("climate.adjustEvaporation")}
                values={adjustments.evaporation}
                onChange={(evaporation2) =>
                  onChangeAdjustments({ ...adjustments, evaporation: evaporation2 })
                }
              />
              <AdjustmentRow
                label={t("climate.adjustRainfall")}
                values={adjustments.rainfall}
                onChange={(rainfall) => onChangeAdjustments({ ...adjustments, rainfall })}
              />
              <AdjustmentRow
                label={t("climate.adjustConductivity")}
                values={adjustments.conductivity}
                onChange={(conductivity) => onChangeAdjustments({ ...adjustments, conductivity })}
              />
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function MonthlyTable({
  values,
  onChange,
}: {
  values: number[];
  onChange: (values: number[]) => void;
}) {
  const t = useT();
  return (
    <div className="named-collection-points-table-wrap">
      <table className="named-collection-points-table">
        <thead>
          <tr>
            {MONTH_KEYS.map((key) => (
              <th key={key}>{t(key)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {values.map((value, i) => (
              <td key={i}>
                <NumberInput
                  value={value}
                  onChange={(v) => {
                    const next = [...values];
                    next[i] = v;
                    onChange(next);
                  }}
                />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function AdjustmentRow({
  label,
  values,
  onChange,
}: {
  label: string;
  values: number[];
  onChange: (values: number[]) => void;
}) {
  return (
    <tr>
      <td className="pattern-table-label-cell">{label}</td>
      {values.map((value, i) => (
        <td key={i}>
          <NumberInput
            value={value}
            onChange={(v) => {
              const next = [...values];
              next[i] = v;
              onChange(next);
            }}
          />
        </td>
      ))}
    </tr>
  );
}
